import { Module } from '../types';

export const ROUTER_NAVIGATION_MODULE: Module = {
  id: "intermediate-router",
  title: "React Router & Navigation",
  description: "Master client-side routing with React Router for building single-page applications with navigation.",
  duration: "2 hours",
  estimatedTime: 120,
  difficulty: "intermediate",
  content: `
# React Router & Navigation

## Learning Objectives
- Set up React Router for single-page applications
- Create dynamic routes with parameters
- Implement nested routes and layouts
- Handle navigation programmatically
- Manage route protection and authentication
- Optimize routing with code splitting

## Getting Started with React Router

### Basic Setup
\`\`\`jsx
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    </BrowserRouter>
  );
}
\`\`\`

### Navigation with Link Components
\`\`\`jsx
import { Link, NavLink } from 'react-router-dom';

function Navigation() {
  return (
    <nav>
      <Link to="/">Home</Link>
      <Link to="/about">About</Link>
      <Link to="/contact">Contact</Link>
      
      {/* NavLink provides active class */}
      <NavLink 
        to="/products" 
        className={({ isActive }) => isActive ? 'active' : ''}
      >
        Products
      </NavLink>
    </nav>
  );
}
\`\`\`

## Dynamic Routes and Parameters

### Route Parameters
\`\`\`jsx
import { useParams } from 'react-router-dom';

// Route definition
<Route path="/user/:id" element={<UserProfile />} />
<Route path="/user/:id/posts/:postId" element={<UserPost />} />

// Component using parameters
function UserProfile() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    fetchUser(id).then(setUser);
  }, [id]);
  
  return user ? <div>User: {user.name}</div> : <div>Loading...</div>;
}

function UserPost() {
  const { id, postId } = useParams();
  
  return <div>User {id}, Post {postId}</div>;
}
\`\`\`

### Query Parameters
\`\`\`jsx
import { useSearchParams } from 'react-router-dom';

function ProductList() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const category = searchParams.get('category');
  const sort = searchParams.get('sort');
  
  const updateFilters = (newCategory, newSort) => {
    setSearchParams({
      category: newCategory,
      sort: newSort
    });
  };
  
  return (
    <div>
      <h2>Products</h2>
      <p>Category: {category}, Sort: {sort}</p>
      
      <button onClick={() => updateFilters('electronics', 'price')}>
        Electronics by Price
      </button>
    </div>
  );
}

// URL: /products?category=electronics&sort=price
\`\`\`

## Nested Routes and Layouts

### Layout Components
\`\`\`jsx
import { Outlet } from 'react-router-dom';

function DashboardLayout() {
  return (
    <div className="dashboard">
      <header>Dashboard Header</header>
      <nav>
        <Link to="/dashboard">Overview</Link>
        <Link to="/dashboard/analytics">Analytics</Link>
        <Link to="/dashboard/settings">Settings</Link>
      </nav>
      <main>
        <Outlet /> {/* Child routes render here */}
      </main>
    </div>
  );
}

// Route configuration
<Route path="/dashboard" element={<DashboardLayout />}>
  <Route index element={<DashboardOverview />} />
  <Route path="analytics" element={<Analytics />} />
  <Route path="settings" element={<Settings />} />
</Route>
\`\`\`

### Nested Route Structure
\`\`\`jsx
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="products" element={<ProductsLayout />}>
            <Route index element={<ProductList />} />
            <Route path=":id" element={<ProductDetail />} />
            <Route path=":id/reviews" element={<ProductReviews />} />
          </Route>
          <Route path="about" element={<AboutPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

// URLs:
// / → Layout + HomePage
// /products → Layout + ProductsLayout + ProductList
// /products/123 → Layout + ProductsLayout + ProductDetail
// /products/123/reviews → Layout + ProductsLayout + ProductReviews
\`\`\`

## Programmatic Navigation

### Using useNavigate Hook
\`\`\`jsx
import { useNavigate } from 'react-router-dom';

function LoginForm() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await login(credentials);
      // Navigate to dashboard after successful login
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
    }
  };
  
  const goBack = () => {
    navigate(-1); // Go back one page in history
  };
  
  const replaceCurrentPage = () => {
    navigate('/new-page', { replace: true }); // Replace current page in history
  };
  
  return (
    <form onSubmit={handleSubmit}>
      {/* form fields */}
      <button type="submit">Login</button>
      <button type="button" onClick={goBack}>Go Back</button>
    </form>
  );
}
\`\`\`

### Navigation with State
\`\`\`jsx
function ProductCard({ product }) {
  const navigate = useNavigate();
  
  const viewProduct = () => {
    navigate(\`/products/\${product.id}\`, {
      state: { from: 'product-list', product }
    });
  };
  
  return (
    <div onClick={viewProduct}>
      <h3>{product.name}</h3>
      <p>{product.price}</p>
    </div>
  );
}

function ProductDetail() {
  const location = useLocation();
  const { product, from } = location.state || {};
  
  return (
    <div>
      {from && <p>Came from: {from}</p>}
      <h1>{product?.name}</h1>
    </div>
  );
}
\`\`\`

## Route Protection and Authentication

### Protected Route Component
\`\`\`jsx
import { Navigate, useLocation } from 'react-router-dom';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
}

// Usage in routes
<Route 
  path="/dashboard" 
  element={
    <ProtectedRoute>
      <Dashboard />
    </ProtectedRoute>
  } 
/>
\`\`\`

### Role-Based Access Control
\`\`\`jsx
function RequireRole({ allowedRoles, children }) {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  return children;
}

// Usage
<Route 
  path="/admin" 
  element={
    <RequireRole allowedRoles={['admin']}>
      <AdminPanel />
    </RequireRole>
  } 
/>
\`\`\`

## Advanced Routing Patterns

### Code Splitting with Lazy Loading
\`\`\`jsx
import { lazy, Suspense } from 'react';

// Lazy load components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route 
          path="/dashboard/*" 
          element={
            <Suspense fallback={<div>Loading Dashboard...</div>}>
              <Dashboard />
            </Suspense>
          } 
        />
      </Routes>
    </BrowserRouter>
  );
}
\`\`\`

### Custom Route Hooks
\`\`\`jsx
// Custom hook for route data
function useRouteData() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  return {
    params,
    query: new URLSearchParams(location.search),
    pathname: location.pathname,
    navigate,
    isActive: (path) => location.pathname === path
  };
}

// Usage
function MyComponent() {
  const { params, query, navigate } = useRouteData();
  
  const productId = params.id;
  const sortBy = query.get('sort');
  
  return <div>Product {productId}, sorted by {sortBy}</div>;
}
\`\`\`

### Error Boundaries with Routes
\`\`\`jsx
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error('Route error:', error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return <div>Something went wrong with this page.</div>;
    }
    
    return this.props.children;
  }
}

// Wrap routes with error boundary
<Route 
  path="/dashboard" 
  element={
    <RouteErrorBoundary>
      <Dashboard />
    </RouteErrorBoundary>
  } 
/>
\`\`\`

## Best Practices

1. **Use layout routes** for shared UI components
2. **Implement route protection** early in development
3. **Lazy load routes** for better performance
4. **Use descriptive route paths** that match your app structure
5. **Handle loading and error states** for async routes
6. **Preserve scroll position** when appropriate
7. **Use relative paths** in nested routes when possible

## Common Patterns

### Breadcrumb Navigation
\`\`\`jsx
function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);
  
  return (
    <nav>
      <Link to="/">Home</Link>
      {pathnames.map((pathname, index) => {
        const routeTo = \`/\${pathnames.slice(0, index + 1).join('/')}\`;
        const isLast = index === pathnames.length - 1;
        
        return (
          <span key={pathname}>
            {' > '}
            {isLast ? (
              <span>{pathname}</span>
            ) : (
              <Link to={routeTo}>{pathname}</Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
\`\`\`

### Route-Based Code Organization
\`\`\`
src/
  pages/
    HomePage.jsx
    AboutPage.jsx
    ProductsPage.jsx
    ProductDetailPage.jsx
  layouts/
    AppLayout.jsx
    DashboardLayout.jsx
  components/
    Navigation.jsx
    ProtectedRoute.jsx
  hooks/
    useRouteData.js
    useAuth.js
\`\`\`
`,
  videoUrl: "https://example.com/react-router",
  codeExamples: [
    {
      title: "Basic Router Setup",
      code: `import { BrowserRouter, Routes, Route } from 'react-router-dom';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/users/:id" element={<UserProfile />} />
      </Routes>
    </BrowserRouter>
  );
}`,
      explanation: "Basic setup for React Router with static and dynamic routes",
      language: "jsx"
    }
  ],
  practice: {
    task: "Build a blog application with React Router that includes: home page, blog post list, individual post pages with comments, and an admin section. Implement route protection for admin pages.",
    starter: `import { BrowserRouter, Routes, Route } from 'react-router-dom';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Add your routes here */}
      </Routes>
    </BrowserRouter>
  );
}

function BlogLayout() {
  // Implement layout with navigation
}

function BlogPost() {
  // Use useParams to get post ID
}

function AdminRoute({ children }) {
  // Implement admin protection
}`,
    solution: `import { BrowserRouter, Routes, Route, Link, Outlet, useParams, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<BlogLayout />}>
          <Route index element={<HomePage />} />
          <Route path="posts" element={<PostList />} />
          <Route path="posts/:id" element={<BlogPost />} />
          <Route path="posts/:id/comments" element={<PostComments />} />
          <Route path="admin/*" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function BlogLayout() {
  return (
    <div>
      <nav>
        <Link to="/">Home</Link>
        <Link to="/posts">Posts</Link>
        <Link to="/admin">Admin</Link>
      </nav>
      <main>
        <Outlet />
      </main>
    </div>
  );
}

function BlogPost() {
  const { id } = useParams();
  return (
    <div>
      <h1>Blog Post {id}</h1>
      <Link to={\`/posts/\${id}/comments\`}>View Comments</Link>
    </div>
  );
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  return user?.role === 'admin' ? children : <Navigate to="/" replace />;
}`,
    hints: [
      "Use nested routes with Outlet for layouts",
      "Extract the post ID using useParams",
      "Implement route protection by checking user role",
      "Use Link components for navigation between pages"
    ],
    estimatedTime: 60
  },
  quickCheck: {
    questions: [
      {
        id: "router-q1",
        question: "Which hook is used to access route parameters in React Router?",
        options: ["useRoute", "useParams", "useRouteParams", "useLocation"],
        correctAnswerIndex: 1,
        difficulty: "easy",
        correctAnswer: 1
      },
      {
        id: "router-q2",
        question: "What is the purpose of the Outlet component?",
        options: [
          "To navigate programmatically",
          "To render child routes in nested routing",
          "To protect routes",
          "To handle route errors"
        ],
        correctAnswerIndex: 1,
        difficulty: "medium",
        correctAnswer: 1
      },
      {
        id: "router-q3",
        question: "How do you navigate programmatically in React Router v6?",
        options: [
          "useHistory hook",
          "useNavigate hook",
          "history.push()",
          "window.location"
        ],
        correctAnswerIndex: 1,
        difficulty: "medium",
        correctAnswer: 1
      }
    ]
  }
};
